import { Children, createContext, useContext, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { BUTTON_DEFAULTS, type ButtonVariant, type ButtonTone, type ButtonSize } from '../button'

interface ButtonGroupContextValue {
  variant?: ButtonVariant
  tone?: ButtonTone
  size?: ButtonSize
}

const ButtonGroupContext = createContext<ButtonGroupContextValue | null>(null)
export const useButtonGroupContext = () => useContext(ButtonGroupContext)

/**
 * How anything button-shaped picks its look: its own prop, then the enclosing group's, then its
 * fallback. One resolver, so a Button, a Menu.Trigger and a custom trigger join a group the same way.
 */
export function useGroupedButtonProps(
  own: ButtonGroupContextValue,
  fallback: Required<{ [K in keyof ButtonGroupContextValue]: NonNullable<ButtonGroupContextValue[K]> }>,
) {
  const group = useButtonGroupContext()
  return {
    variant: own.variant ?? group?.variant ?? fallback.variant,
    tone: own.tone ?? group?.tone ?? fallback.tone,
    size: own.size ?? group?.size ?? fallback.size,
  }
}

interface ButtonGroupProps {
  children: ReactNode
  className?: string
  variant?: ButtonVariant
  tone?: ButtonTone
  size?: ButtonSize
  /**
   * `split` joins buttons edge to edge with a divider, each keeping its own ground: one action
   * with a side door, like "Copy link | Open". `shell` draws one frame in `variant` at `size` and
   * sets ghost buttons a size down inside it: a few peers sharing one control, like the thumbs.
   */
  layout?: 'split' | 'shell'
}

type Size = NonNullable<ButtonSize>

/**
 * A shell's buttons sit one size down, inset by half the difference on every side and spaced by
 * the same amount. `xs` has no size under it, so it frames like `sm`.
 */
const SHELL: Record<Size, { inner: Size; box: string }> = {
  xs: { inner: 'xs', box: '[--shell-h:24px] [--shell-inset:2px]' },
  sm: { inner: 'xs', box: '[--shell-h:24px] [--shell-inset:2px]' },
  md: { inner: 'sm', box: '[--shell-h:28px] [--shell-inset:2px]' },
  lg: { inner: 'md', box: '[--shell-h:32px] [--shell-inset:2px]' },
  xl: { inner: 'lg', box: '[--shell-h:40px] [--shell-inset:4px]' },
  '2xl': { inner: 'xl', box: '[--shell-h:48px] [--shell-inset:4px]' },
}

type Variant = NonNullable<ButtonVariant>
type FixedGround = 'on-light' | 'on-dark'

const isFixedGround = (tone: ButtonTone): tone is FixedGround => tone === 'on-light' || tone === 'on-dark'

/** The frame takes the variant's resting look. A hairline drawn inset, so it costs no layout. */
const SHELL_LOOK: Record<Variant, string> = {
  primary: 'bg-element',
  secondary: 'bg-element',
  outline: 'shadow-[inset_0_0_0_var(--border-hairline)_var(--border-strong)]',
  ghost: '',
}

/** On a fixed ground the theme's fills and hairline would swap under it, so the frame is cut from that ground's own ramp. */
const SHELL_LOOK_FIXED: Record<FixedGround, Record<Variant, string>> = {
  'on-light': {
    primary: 'bg-on-light-subtle',
    secondary: 'bg-on-light-subtle',
    outline: 'shadow-[inset_0_0_0_var(--border-hairline)_var(--on-light-border)]',
    ghost: '',
  },
  'on-dark': {
    primary: 'bg-on-dark-subtle',
    secondary: 'bg-on-dark-subtle',
    outline: 'shadow-[inset_0_0_0_var(--border-hairline)_var(--on-dark-border)]',
    ghost: '',
  },
}

/**
 * The seam belongs to the segment on its right, as an inset hairline on that button's left edge. An
 * element between two buttons costs a whole pixel of the pill's width, where the system hairline is
 * half of one. Each class is written out whole because Tailwind scans source text for class names.
 */
const SEAM = {
  theme: '[&_:is(button,a)]:shadow-[inset_var(--border-hairline)_0_0_0_var(--border)]',
  'on-light': '[&_:is(button,a)]:shadow-[inset_var(--border-hairline)_0_0_0_var(--on-light-border)]',
  'on-dark': '[&_:is(button,a)]:shadow-[inset_var(--border-hairline)_0_0_0_var(--on-dark-border)]',
  inverse:
    '[&_:is(button,a)]:shadow-[inset_var(--border-hairline)_0_0_0_var(--on-dark-border)] dark:[&_:is(button,a)]:shadow-[inset_var(--border-hairline)_0_0_0_var(--on-light-border)]',
} as const

const OPPOSITE_GROUND: Record<FixedGround, FixedGround> = { 'on-light': 'on-dark', 'on-dark': 'on-light' }

/**
 * A seam sits on the buttons' background and has to read against it. A fixed-background tone fixes the
 * background whatever the theme: its solid is the opposite colour (black on-light is a dark solid), its
 * quieter variants sit on the background itself. Otherwise only a solid needs a seam of its own, a
 * light one except on danger's dark solid, and every other variant takes the theme's border.
 */
function seamFor(variant: ButtonVariant, tone: ButtonTone): string {
  const solid = variant === 'primary'
  if (isFixedGround(tone)) return SEAM[solid ? OPPOSITE_GROUND[tone] : tone]
  if (!solid) return SEAM.theme
  // The inverse solid is on-light's black in light and on-dark's white in dark.
  if (tone === 'inverse') return SEAM.inverse
  return SEAM[tone === 'danger' ? 'on-dark' : 'on-light']
}

function ButtonShell({ children, className, variant, tone, size }: ButtonGroupProps) {
  const shell = SHELL[size ?? 'md']
  return (
    <ButtonGroupContext.Provider value={{ variant: 'ghost', tone, size: shell.inner }}>
      <div
        className={twMerge(
          'inline-flex items-center gap-[var(--shell-inset)] px-[var(--shell-inset)]',
          'corner-squircle h-[var(--shell-h)] rounded-[calc(var(--shell-h)/2)]',
          shell.box,
          (isFixedGround(tone) ? SHELL_LOOK_FIXED[tone] : SHELL_LOOK)[variant ?? 'outline'],
          className,
        )}
      >
        {children}
      </div>
    </ButtonGroupContext.Provider>
  )
}

function ButtonGroup({ layout = 'split', ...props }: ButtonGroupProps) {
  return layout === 'shell' ? <ButtonShell {...props} /> : <SplitButtonGroup {...props} />
}

function SplitButtonGroup({ children, className, variant, tone, size }: ButtonGroupProps) {
  // An outline group's seam is the buttons' own hairline, shared: each segment drops its left edge
  // and keeps its right. Filled variants have no edge, so each segment after the first draws its own
  // seam on its left. The group's props are only fallbacks for its buttons, which resolve to the
  // Button defaults.
  const resolvedVariant = variant ?? BUTTON_DEFAULTS.variant
  const sharesEdge = resolvedVariant === 'outline'
  const seamClass = seamFor(resolvedVariant, tone ?? BUTTON_DEFAULTS.tone)

  return (
    <ButtonGroupContext.Provider value={{ variant, tone, size }}>
      <div
        className={twMerge(
          'flex items-center',
          // The group is one control, so it takes the press as one. A segment scaling alone would
          // pull away from its neighbour and split the pill.
          'transition-transform duration-150 ease-out active:scale-[0.96] motion-reduce:active:scale-100',
          '[&_:is(button,a)]:active:scale-100',
          className,
        )}
      >
        {Children.toArray(children).map((child, index, arr) => {
          const isOnly = arr.length === 1
          const isFirst = index === 0
          const isLast = index === arr.length - 1

          // `a` as well as `button`: a Button with an href renders an anchor.
          const classes = twMerge(
            !isOnly && isFirst && '[&_:is(button,a)]:rounded-r-none',
            !isOnly && isFirst && !sharesEdge && '[&_:is(button,a)]:border-r-0',
            !isOnly && isLast && '[&_:is(button,a)]:rounded-l-none [&_:is(button,a)]:border-l-0',
            !isFirst && !isLast && '[&_:is(button,a)]:rounded-none [&_:is(button,a)]:border-l-0',
            !isFirst && !isLast && !sharesEdge && '[&_:is(button,a)]:border-r-0',
            !isFirst && !sharesEdge && seamClass,
          )

          return (
            <div key={index} className={classes}>
              {child}
            </div>
          )
        })}
      </div>
    </ButtonGroupContext.Provider>
  )
}

export { ButtonGroup }
